import React, { useState, useEffect } from 'react';
import BackButton from '@/components/BackButton';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Buildings, Stethoscope, BellRinging, Plus, Trash, PencilSimple,
  FilePdf, DownloadSimple, Warning, FloppyDisk, ArrowLeft, ArrowRight
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "/branding/logo.png";

const STATUS_CONFIG = {
  ok:          { ar: 'سارية',     en: 'Valid',           class: 'bg-green-100 text-green-700 border-green-200' },
  warning_60:  { ar: '60 يوم',    en: '60 days',         class: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
  warning_30:  { ar: '30 يوم',    en: '30 days',         class: 'bg-orange-100 text-orange-700 border-orange-200' },
  critical_7:  { ar: '7 أيام',    en: '7 days',          class: 'bg-red-100 text-red-700 border-red-300' },
  expired:     { ar: 'منتهية',    en: 'Expired',         class: 'bg-red-200 text-red-800 border-red-400 font-bold' },
  no_expiry:   { ar: '—',         en: '—',               class: 'bg-gray-100 text-gray-600' },
  invalid:     { ar: '!',         en: '!',               class: 'bg-gray-100 text-gray-600' },
};

const emptyForm = {
  doc_type: '', custom_label: '', document_number: '', issuer: '',
  issue_date: '', expiry_date: '', notes: ''
};

// ─── Reusable staff-file pane (used for both Practitioners & Employees) ──
// Renders three nested levels:
//   1. Department sub-tabs (doctor / nursing / lab / sterilization) — only
//      shown when `scope==='practitioners'`; the Employees tab pins to
//      `non_healthcare`.
//   2. Employee grid on the left listing every staff member in that dept
//      with a licence count + worst-status badge.
//   3. When an employee is selected: 6 doc-category tabs (Demography →
//      Evaluation) with a table of documents for that category.
function PractitionersOrEmployeesPane(props) {
  const {
    scope, staffByDept, docTypes,
    selectedDept, setSelectedDept,
    selectedPract, setSelectedPract,
    practLicenses, setPractLicenses,
    selectedDocCat, setSelectedDocCat,
    openAdd, openEdit, handleDelete, handleDownload,
    docTypeLabel, STATUS_CONFIG, StatusBadge,
    T, language, direction,
    canDelete,
  } = props;

  const depts = scope === 'practitioners'
    ? ['doctor', 'nursing', 'lab', 'sterilization']
    : ['non_healthcare'];
  const currentList = staffByDept[selectedDept] || [];
  const currentEmpIsNonHc = selectedDept === 'non_healthcare';

  const fetchLicenses = async (empId) => {
    const { data } = await axios.get(`${API}/government/practitioner-licenses?employee_id=${empId}`, { withCredentials: true });
    setPractLicenses(data);
  };
  const onPickStaff = async (emp) => {
    setSelectedPract(emp);
    setSelectedDocCat('demography');
    await fetchLicenses(emp.id);
  };

  // Group licences by their category (falls back to 'legacy' bucket for
  // rows created before the refactor)
  const licensesByCategory = React.useMemo(() => {
    const map = {};
    practLicenses.forEach((l) => {
      const cat = l.doc_category || docTypes.staff_categories_flat?.[l.doc_type]?.category || 'legacy';
      (map[cat] = map[cat] || []).push(l);
    });
    return map;
  }, [practLicenses, docTypes]);

  // For the currently active doc category, produce a checklist of
  // (doc-type × existing document) so we can render "missing" rows too.
  const currentCatDocs = React.useMemo(() => {
    const catCfg = docTypes.staff_categories?.[selectedDocCat];
    if (!catCfg) return [];
    // For non-healthcare staff, only show docs flagged non_healthcare=true
    const allowedDocs = catCfg.docs.filter((d) => !currentEmpIsNonHc || d.non_healthcare);
    return allowedDocs.map((d) => {
      const existing = (licensesByCategory[selectedDocCat] || []).find((l) => l.doc_type === d.id);
      return { docType: d, existing };
    });
  }, [selectedDocCat, docTypes, licensesByCategory, currentEmpIsNonHc]);

  const DEPT_LABEL_AR = { doctor: 'الطبية', nursing: 'التمريض', lab: 'المعمل', sterilization: 'التعقيم', non_healthcare: 'الموظفون' };
  const DEPT_LABEL_EN = { doctor: 'Doctors', nursing: 'Nursing', lab: 'Lab', sterilization: 'Sterilization', non_healthcare: 'Employees' };

  return (
    <div className="space-y-4" dir={direction}>
      {/* Department sub-tabs (only for practitioners) */}
      {scope === 'practitioners' && (
        <Tabs value={selectedDept} onValueChange={(v) => { setSelectedDept(v); setSelectedPract(null); }} dir={direction}>
          <TabsList className="bg-white border border-[#B0DDE8] p-1 h-auto">
            {depts.map((d) => (
              <TabsTrigger
                key={d}
                value={d}
                className="data-[state=active]:bg-[#2A8FA8] data-[state=active]:text-white px-4 py-1.5 text-sm gap-2"
                data-testid={`dept-tab-${d}`}
              >
                {language === 'ar' ? DEPT_LABEL_AR[d] : DEPT_LABEL_EN[d]}
                <Badge variant="outline" className="text-[10px]">{staffByDept[d]?.length || 0}</Badge>
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      )}

      <div className="grid md:grid-cols-4 gap-4">
        {/* Staff picker */}
        <Card className="md:col-span-1 border border-[#B0DDE8]">
          <CardHeader className="py-3">
            <CardTitle className="text-base text-[#2A8FA8]">
              {language === 'ar' ? DEPT_LABEL_AR[selectedDept] : DEPT_LABEL_EN[selectedDept]}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-2 max-h-[640px] overflow-y-auto space-y-1">
            {currentList.length === 0 ? (
              <p className="text-xs text-[#2A8FA8]/60 text-center py-4">{T('لا يوجد موظفون في هذا القسم', 'No employees in this section')}</p>
            ) : currentList.map((p) => {
              const cfg = STATUS_CONFIG[p.worst_status] || STATUS_CONFIG.no_expiry;
              const active = selectedPract?.id === p.id;
              return (
                <button key={p.id} onClick={() => onPickStaff(p)}
                  className={`w-full text-start p-2 rounded-md transition-all ${active ? 'bg-[#2A8FA8] text-white' : 'hover:bg-[#E8F7FB]'}`}
                  data-testid={`staff-${p.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className={`font-medium text-sm truncate ${active ? 'text-white' : 'text-[#2A8FA8]'}`}>{p.name}</p>
                      <p className={`text-xs truncate ${active ? 'text-white/80' : 'text-[#2A8FA8]/60'}`}>
                        {p.position || p.department || '-'}
                      </p>
                    </div>
                    {p.docs_count > 0 && (
                      <Badge className={`${active ? 'bg-white/20 text-white' : cfg.class} text-[10px]`}>
                        {p.docs_count}
                      </Badge>
                    )}
                  </div>
                </button>
              );
            })}
          </CardContent>
        </Card>

        {/* Selected staff — 6 doc-category tabs */}
        <Card className="md:col-span-3 border border-[#B0DDE8]">
          <CardHeader className="flex flex-row items-center justify-between py-3">
            <CardTitle className="text-base text-[#2A8FA8]">
              {selectedPract ? selectedPract.name : T('اختر موظفاً', 'Select an employee')}
            </CardTitle>
            {selectedPract && (
              <Button
                onClick={() => openAdd('practitioner', selectedPract, selectedDocCat)}
                size="sm"
                className="bg-[#2A8FA8] hover:bg-[#1F7A90] text-white gap-2"
                data-testid="add-staff-doc-btn"
              >
                <Plus size={14} weight="bold" /> {T('إضافة مستند', 'Add Document')}
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {!selectedPract ? (
              <p className="text-center text-[#2A8FA8]/60 py-12 text-sm">{T('اختر موظفاً من القائمة', 'Pick an employee from the list')}</p>
            ) : (
              <Tabs value={selectedDocCat} onValueChange={setSelectedDocCat} dir={direction}>
                <TabsList className="bg-[#E8F7FB] p-1 flex flex-wrap h-auto">
                  {Object.entries(docTypes.staff_categories || {}).map(([catId, cat]) => {
                    const cnt = (licensesByCategory[catId] || []).length;
                    return (
                      <TabsTrigger
                        key={catId}
                        value={catId}
                        className="data-[state=active]:bg-[#2A8FA8] data-[state=active]:text-white text-xs px-3 py-1.5 gap-1.5"
                        data-testid={`doccat-${catId}`}
                      >
                        {cat[language]}
                        {cnt > 0 && <Badge variant="outline" className="text-[10px] bg-white">{cnt}</Badge>}
                      </TabsTrigger>
                    );
                  })}
                </TabsList>

                {Object.keys(docTypes.staff_categories || {}).map((catId) => (
                  <TabsContent key={catId} value={catId} className="mt-4">
                    <Table className="[&_th]:text-center [&_td]:text-center [&_th]:align-middle [&_td]:align-middle">
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-[#2A8FA8]">{T('المستند', 'Document')}</TableHead>
                          <TableHead className="text-[#2A8FA8]">{T('الرقم', 'Number')}</TableHead>
                          <TableHead className="text-[#2A8FA8]">{T('الانتهاء', 'Expires')}</TableHead>
                          <TableHead className="text-[#2A8FA8]">{T('الحالة', 'Status')}</TableHead>
                          <TableHead className="text-[#2A8FA8]">{T('إجراءات', 'Actions')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {currentCatDocs.map(({ docType: dt, existing: d }) => (
                          <TableRow key={dt.id} className="border-[#B0DDE8]" data-testid={`staffdoc-row-${dt.id}`}>
                            <TableCell>
                              <p className="font-medium text-[#2A8FA8] text-sm">{dt[language]}</p>
                            </TableCell>
                            <TableCell className="text-sm text-[#2A8FA8]/70" dir="ltr">{d?.document_number || '—'}</TableCell>
                            <TableCell className="text-sm text-[#2A8FA8]" dir="ltr">{d?.expiry_date || '—'}</TableCell>
                            <TableCell>
                              {d
                                ? <StatusBadge status={d.status} days={d.days_until_expiry} />
                                : <Badge variant="outline" className="text-[10px] text-gray-500 border-gray-300">{T('غير مرفوع', 'Not uploaded')}</Badge>}
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-1 justify-center">
                                {d ? (
                                  <>
                                    {d.file && (
                                      <Button variant="ghost" size="icon" className="h-7 w-7 text-[#2A8FA8]" onClick={() => handleDownload('practitioner', d.id, d.file.original_name)} data-testid={`download-staffdoc-${d.id}`}>
                                        <DownloadSimple size={14} />
                                      </Button>
                                    )}
                                    <Button variant="ghost" size="icon" className="h-7 w-7 text-[#2A8FA8] hover:bg-[#E8F7FB]" onClick={() => openEdit('practitioner', d)} data-testid={`edit-staffdoc-${d.id}`}>
                                      <PencilSimple size={14} />
                                    </Button>
                                    {canDelete && (
                                      <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:bg-red-50" onClick={() => handleDelete('practitioner', d.id)} data-testid={`delete-staffdoc-${d.id}`}>
                                        <Trash size={14} />
                                      </Button>
                                    )}
                                  </>
                                ) : (
                                  <Button variant="ghost" size="sm" className="h-7 text-[#2A8FA8] hover:bg-[#E8F7FB] gap-1 text-xs"
                                    onClick={() => openAdd('practitioner', selectedPract, catId, dt.id)}
                                    data-testid={`upload-staffdoc-${dt.id}`}>
                                    <Plus size={12} /> {T('رفع', 'Upload')}
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TabsContent>
                ))}
              </Tabs>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default function GovernmentAffairs() {
  const { language, direction } = useLanguage();
  const { user } = useAuth();
  // v1.5.42 — Delete is admin-only. A plain Government Officer (scoped
  // delegate) sees the module but can only create + update. The
  // backend enforces the same rule with `get_admin_user` on the two
  // DELETE endpoints; this flag drives the UX so the officer never
  // sees a trash button that would return 403 when clicked.
  const canDelete = !!(
    user && (
      user.role === 'admin' ||
      user.role === 'general_manager' ||
      user.is_general_manager ||
      user.is_finance_manager
    )
  );
  const navigate = useNavigate();
  const T = (ar, en) => (language === 'ar' ? ar : en);

  const [tab, setTab] = useState('establishment');
  const [docTypes, setDocTypes] = useState({
    establishment: {},
    practitioner: {},
    staff_categories: {},
    staff_categories_flat: {},
    departments: {},
  });
  const [estabDocs, setEstabDocs] = useState([]);
  // Staff grouped by department (doctor/nursing/lab/sterilization/non_healthcare)
  const [staffByDept, setStaffByDept] = useState({
    doctor: [], nursing: [], lab: [], sterilization: [], non_healthcare: [],
  });
  const [selectedDept, setSelectedDept] = useState('doctor');
  const [selectedPract, setSelectedPract] = useState(null);
  const [practLicenses, setPractLicenses] = useState([]);
  // Which of the 6 document sub-tabs is active for the selected staff
  const [selectedDocCat, setSelectedDocCat] = useState('demography');
  const [alerts, setAlerts] = useState({ establishment: [], practitioner: [], summary: { total: 0 } });
  const [loading, setLoading] = useState(true);

  const [showDialog, setShowDialog] = useState(false);
  const [dialogScope, setDialogScope] = useState('establishment'); // or 'practitioner'
  // When adding via the "Upload" shortcut inside a doc-category tab, we
  // preset the category so the doc-type <Select> is pre-scoped.
  const [dialogPresetCategory, setDialogPresetCategory] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [pickedFile, setPickedFile] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [types, estab, staff, alertsRes] = await Promise.all([
        axios.get(`${API}/government/doc-types`, { withCredentials: true }),
        axios.get(`${API}/government/establishment-docs`, { withCredentials: true }),
        axios.get(`${API}/government/staff`, { withCredentials: true }),
        axios.get(`${API}/government/alerts`, { withCredentials: true }),
      ]);
      setDocTypes(types.data);
      setEstabDocs(estab.data);
      setStaffByDept(staff.data);
      setAlerts(alertsRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchPractLicenses = async (empId) => {
    const { data } = await axios.get(`${API}/government/practitioner-licenses?employee_id=${empId}`, { withCredentials: true });
    setPractLicenses(data);
  };

  const openAdd = (scope, staff = null, presetCategory = null, presetDocType = null) => {
    setDialogScope(scope);
    setEditing(null);
    // If the caller pre-selected a staff member (from the new pane), lock
    // the dialog to them. Same idea for the doc-type when the "Upload"
    // shortcut is used inside a specific tab row.
    if (staff && scope === 'practitioner') setSelectedPract(staff);
    setForm({ ...emptyForm, doc_type: presetDocType || '' });
    setDialogPresetCategory(presetCategory);
    setPickedFile(null);
    setShowDialog(true);
  };

  const openEdit = (scope, doc) => {
    setDialogScope(scope);
    setEditing(doc);
    setForm({
      doc_type: doc.doc_type || '',
      custom_label: doc.custom_label || '',
      document_number: doc.document_number || '',
      issuer: doc.issuer || '',
      issue_date: doc.issue_date || '',
      expiry_date: doc.expiry_date || '',
      notes: doc.notes || '',
    });
    setPickedFile(null);
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!form.doc_type) { toast.error(T('اختر نوع الوثيقة', 'Select document type')); return; }
    if (dialogScope === 'practitioner' && !selectedPract && !editing) {
      toast.error(T('اختر الممارس', 'Select practitioner')); return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v ?? ''));
      if (dialogScope === 'practitioner') fd.append('employee_id', editing?.employee_id || selectedPract?.id);
      if (pickedFile) fd.append('file', pickedFile);

      const url = dialogScope === 'establishment'
        ? `${API}/government/establishment-docs${editing ? '/' + editing.id : ''}`
        : `${API}/government/practitioner-licenses${editing ? '/' + editing.id : ''}`;

      const { data: saved } = await axios({
        url, method: editing ? 'put' : 'post', data: fd, withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      // Show compression savings when the backend re-encoded the upload.
      // `file.original_size` is the pre-compression byte-count; `file.size`
      // is what we ultimately stored on disk.
      const f = saved?.file;
      if (f && f.original_size && f.size && f.original_size > f.size) {
        const savedBytes = f.original_size - f.size;
        const pct = Math.round((savedBytes / f.original_size) * 100);
        const kb = (b) => `${(b / 1024).toFixed(1)} KB`;
        toast.success(
          T(
            `تمت إضافة الوثيقة — تم ضغط الملف بنسبة ${pct}% (${kb(f.original_size)} → ${kb(f.size)})`,
            `Added — file compressed by ${pct}% (${kb(f.original_size)} → ${kb(f.size)})`
          ),
          { duration: 6000 }
        );
      } else {
        toast.success(editing ? T('تم تحديث الوثيقة', 'Updated') : T('تمت إضافة الوثيقة', 'Added'));
      }
      setShowDialog(false);
      await fetchAll();
      if (dialogScope === 'practitioner' && selectedPract) await fetchPractLicenses(selectedPract.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || T('حدث خطأ', 'Error'));
    } finally { setSaving(false); }
  };

  const handleDelete = async (scope, id) => {
    if (!window.confirm(T('هل أنت متأكد من الحذف؟', 'Are you sure you want to delete?'))) return;
    try {
      const url = scope === 'establishment'
        ? `${API}/government/establishment-docs/${id}`
        : `${API}/government/practitioner-licenses/${id}`;
      await axios.delete(url, { withCredentials: true });
      toast.success(T('تم الحذف', 'Deleted'));
      await fetchAll();
      if (scope === 'practitioner' && selectedPract) await fetchPractLicenses(selectedPract.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };

  const handleDownload = async (scope, id, originalName) => {
    try {
      const url = scope === 'establishment'
        ? `${API}/government/establishment-docs/${id}/file`
        : `${API}/government/practitioner-licenses/${id}/file`;
      const resp = await axios.get(url, { withCredentials: true, responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement('a');
      a.href = blobUrl; a.download = originalName || 'document'; document.body.appendChild(a); a.click(); a.remove();
    } catch { toast.error(T('فشل التنزيل', 'Download failed')); }
  };

  const docTypeLabel = (scope, type, custom) => {
    if (type === 'other' && custom) return custom;
    // New grouped staff catalogue takes precedence
    if (docTypes.staff_categories_flat?.[type]) {
      return docTypes.staff_categories_flat[type][language] || type;
    }
    return docTypes[scope]?.[type]?.[language] || type;
  };

  const StatusBadge = React.useCallback(({ status, days }) => {
    const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.ok;
    return (
      <Badge className={`${cfg.class} text-xs`}>
        {status === 'expired'
          ? T(`منتهية منذ ${Math.abs(days)} يوم`, `Expired ${Math.abs(days)}d ago`)
          : status === 'no_expiry' ? '—'
          : T(`${days} يوم`, `${days}d`)}
      </Badge>
    );
  }, [T]);

  return (
    <div className="min-h-screen bg-[#F0FAFD]" data-testid="government-page">
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-sm shadow-sm border-b border-[#B0DDE8] py-4">
        <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <img src={LOGO_URL} alt="Lucent" className="h-10 object-contain" />
          <BackButton className="text-[#2A8FA8]" />
        </div>
      </header>

      <main className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <div>
          <h1 className="text-3xl font-semibold text-[#2A8FA8]">{T('العلاقات الحكومية', 'Government Affairs')}</h1>
          <p className="text-sm text-[#2A8FA8]/60 mt-1">{T('تراخيص المنشأة والممارسين الصحيين والتنبيهات', 'Establishment licenses, practitioner licenses, and alerts')}</p>
        </div>

        <Tabs value={tab} onValueChange={setTab} dir={direction}>
          <TabsList className="bg-[#E8F7FB] border border-[#B0DDE8] p-1 h-auto">
            <TabsTrigger value="establishment" className="data-[state=active]:bg-[#2A8FA8] data-[state=active]:text-white px-6 py-2 gap-2" data-testid="tab-establishment">
              <Buildings size={18} weight="duotone" />
              {T('المنشأة', 'Establishment')}
              <Badge variant="outline" className="ms-1 text-[10px]">{estabDocs.length}</Badge>
            </TabsTrigger>
            <TabsTrigger value="practitioners" className="data-[state=active]:bg-[#2A8FA8] data-[state=active]:text-white px-6 py-2 gap-2" data-testid="tab-practitioners">
              <Stethoscope size={18} weight="duotone" />
              {T('الممارسون الصحيون', 'Practitioners')}
              <Badge variant="outline" className="ms-1 text-[10px]">
                {(staffByDept.doctor?.length || 0) + (staffByDept.nursing?.length || 0) + (staffByDept.lab?.length || 0) + (staffByDept.sterilization?.length || 0)}
              </Badge>
            </TabsTrigger>
            <TabsTrigger value="employees" className="data-[state=active]:bg-[#2A8FA8] data-[state=active]:text-white px-6 py-2 gap-2" data-testid="tab-employees">
              <Buildings size={18} weight="duotone" />
              {T('الموظفون', 'Employees')}
              <Badge variant="outline" className="ms-1 text-[10px]">{staffByDept.non_healthcare?.length || 0}</Badge>
            </TabsTrigger>
            <TabsTrigger value="alerts" className="data-[state=active]:bg-[#2A8FA8] data-[state=active]:text-white px-6 py-2 gap-2" data-testid="tab-alerts">
              <BellRinging size={18} weight="duotone" className={alerts.summary.total > 0 ? 'animate-pulse text-red-500' : ''} />
              {T('التنبيهات', 'Alerts')}
              {alerts.summary.total > 0 && <Badge className="ms-1 bg-red-500 text-white text-[10px]">{alerts.summary.total}</Badge>}
            </TabsTrigger>
          </TabsList>

          {/* ============ TAB 1: Establishment ============ */}
          <TabsContent value="establishment" className="space-y-4 mt-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-[#2A8FA8]">{T('تراخيص المنشأة', 'Establishment Licenses')}</h2>
              <Button onClick={() => openAdd('establishment')} className="bg-[#2A8FA8] hover:bg-[#1F7A90] text-white gap-2" data-testid="add-estab-btn">
                <Plus size={18} weight="bold" /> {T('إضافة وثيقة', 'Add Document')}
              </Button>
            </div>

            <Card className="border border-[#B0DDE8]">
              <CardContent className="pt-6">
                {loading ? (
                  <div className="flex items-center justify-center h-32"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2A8FA8]" /></div>
                ) : (
                  <Table data-testid="estab-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-start text-[#2A8FA8]">{T('الوثيقة', 'Document')}</TableHead>
                        <TableHead className="text-center text-[#2A8FA8]">{T('الرقم', 'Number')}</TableHead>
                        <TableHead className="text-center text-[#2A8FA8]">{T('جهة الإصدار', 'Issuer')}</TableHead>
                        <TableHead className="text-center text-[#2A8FA8]">{T('الإصدار', 'Issued')}</TableHead>
                        <TableHead className="text-center text-[#2A8FA8]">{T('الانتهاء', 'Expires')}</TableHead>
                        <TableHead className="text-center text-[#2A8FA8]">{T('الحالة', 'Status')}</TableHead>
                        <TableHead className="text-center text-[#2A8FA8]">{T('إجراءات', 'Actions')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {estabDocs.length === 0 ? (
                        <TableRow><TableCell colSpan={7} className="text-center py-8 text-[#2A8FA8]/60">{T('لا توجد وثائق. أضف أول ترخيص.', 'No documents yet. Add your first license.')}</TableCell></TableRow>
                      ) : estabDocs.map(d => (
                        <TableRow key={d.id} className="border-[#B0DDE8]" data-testid={`estab-row-${d.id}`}>
                          <TableCell className="font-medium text-[#2A8FA8]">{docTypeLabel('establishment', d.doc_type, d.custom_label)}</TableCell>
                          <TableCell className="text-center text-[#2A8FA8]/70 text-sm" dir="ltr">{d.document_number || '-'}</TableCell>
                          <TableCell className="text-center text-[#2A8FA8]/70 text-sm">{d.issuer || '-'}</TableCell>
                          <TableCell className="text-center text-[#2A8FA8]/70 text-sm" dir="ltr">{d.issue_date || '-'}</TableCell>
                          <TableCell className="text-center text-[#2A8FA8] text-sm font-medium" dir="ltr">{d.expiry_date || '-'}</TableCell>
                          <TableCell className="text-center"><StatusBadge status={d.status} days={d.days_until_expiry} /></TableCell>
                          <TableCell className="text-center">
                            <div className="flex gap-1 justify-center">
                              {d.file && (
                                <Button variant="ghost" size="icon" className="h-7 w-7 text-[#2A8FA8]" onClick={() => handleDownload('establishment', d.id, d.file.original_name)} data-testid={`download-estab-${d.id}`}>
                                  <DownloadSimple size={14} />
                                </Button>
                              )}
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-[#2A8FA8] hover:bg-[#E8F7FB]" onClick={() => openEdit('establishment', d)} data-testid={`edit-estab-${d.id}`}>
                                <PencilSimple size={14} />
                              </Button>
                              {canDelete && (
                                <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:bg-red-50" onClick={() => handleDelete('establishment', d.id)} data-testid={`delete-estab-${d.id}`}>
                                  <Trash size={14} />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ============ TAB 2: Practitioners (grouped by department) ============ */}
          <TabsContent value="practitioners" className="space-y-4 mt-4">
            <PractitionersOrEmployeesPane
              scope="practitioners"
              staffByDept={staffByDept}
              docTypes={docTypes}
              selectedDept={selectedDept}
              setSelectedDept={setSelectedDept}
              selectedPract={selectedPract}
              setSelectedPract={setSelectedPract}
              practLicenses={practLicenses}
              setPractLicenses={setPractLicenses}
              selectedDocCat={selectedDocCat}
              setSelectedDocCat={setSelectedDocCat}
              openAdd={openAdd}
              openEdit={openEdit}
              handleDelete={handleDelete}
              canDelete={canDelete}
              handleDownload={handleDownload}
              docTypeLabel={docTypeLabel}
              STATUS_CONFIG={STATUS_CONFIG}
              StatusBadge={StatusBadge}
              T={T}
              language={language}
              direction={direction}
            />
          </TabsContent>

          {/* ============ TAB 3: Non-healthcare Employees ============ */}
          <TabsContent value="employees" className="space-y-4 mt-4">
            <PractitionersOrEmployeesPane
              scope="employees"
              staffByDept={staffByDept}
              docTypes={docTypes}
              selectedDept="non_healthcare"
              setSelectedDept={() => {}}
              selectedPract={selectedPract}
              setSelectedPract={setSelectedPract}
              practLicenses={practLicenses}
              setPractLicenses={setPractLicenses}
              selectedDocCat={selectedDocCat}
              setSelectedDocCat={setSelectedDocCat}
              openAdd={openAdd}
              openEdit={openEdit}
              handleDelete={handleDelete}
              canDelete={canDelete}
              handleDownload={handleDownload}
              docTypeLabel={docTypeLabel}
              STATUS_CONFIG={STATUS_CONFIG}
              StatusBadge={StatusBadge}
              T={T}
              language={language}
              direction={direction}
            />
          </TabsContent>

          {/* ============ TAB 3: Alerts ============ */}
          <TabsContent value="alerts" className="space-y-4 mt-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="alerts-summary">
              <div className="p-4 bg-red-200 rounded-lg text-center">
                <p className="text-xs text-red-800 font-bold">{T('منتهية', 'Expired')}</p>
                <p className="text-3xl font-extrabold text-red-800">{alerts.summary.expired || 0}</p>
              </div>
              <div className="p-4 bg-red-100 rounded-lg text-center">
                <p className="text-xs text-red-700 font-bold">{T('خلال 7 أيام', 'Within 7d')}</p>
                <p className="text-3xl font-extrabold text-red-700">{alerts.summary.critical_7 || 0}</p>
              </div>
              <div className="p-4 bg-orange-100 rounded-lg text-center">
                <p className="text-xs text-orange-700 font-bold">{T('خلال 30 يوم', 'Within 30d')}</p>
                <p className="text-3xl font-extrabold text-orange-700">{alerts.summary.warning_30 || 0}</p>
              </div>
              <div className="p-4 bg-yellow-100 rounded-lg text-center">
                <p className="text-xs text-yellow-700 font-bold">{T('خلال 60 يوم', 'Within 60d')}</p>
                <p className="text-3xl font-extrabold text-yellow-700">{alerts.summary.warning_60 || 0}</p>
              </div>
            </div>

            <Card className="border border-[#B0DDE8]">
              <CardHeader><CardTitle className="text-base text-[#2A8FA8] flex items-center gap-2"><Buildings size={18} weight="duotone" /> {T('وثائق المنشأة', 'Establishment Documents')}</CardTitle></CardHeader>
              <CardContent>
                {alerts.establishment.length === 0 ? (
                  <p className="text-center text-[#2A8FA8]/60 py-6 text-sm">{T('لا توجد تنبيهات للمنشأة', 'No establishment alerts')}</p>
                ) : (
                  <div className="space-y-2">
                    {alerts.establishment.map(d => (
                      <div key={d.id} className="flex items-center gap-3 p-3 bg-white border border-[#B0DDE8] rounded-lg" data-testid={`alert-estab-${d.id}`}>
                        <Warning size={20} weight="duotone" className={d.status === 'expired' ? 'text-red-600' : d.status === 'critical_7' ? 'text-red-500' : d.status === 'warning_30' ? 'text-orange-500' : 'text-yellow-500'} />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-[#2A8FA8] text-sm">{language === 'ar' ? d.doc_type_label_ar : d.doc_type_label_en}</p>
                          <p className="text-xs text-[#2A8FA8]/60" dir="ltr">{d.document_number || '-'} • {d.expiry_date}</p>
                        </div>
                        <StatusBadge status={d.status} days={d.days_until_expiry} />
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="border border-[#B0DDE8]">
              <CardHeader><CardTitle className="text-base text-[#2A8FA8] flex items-center gap-2"><Stethoscope size={18} weight="duotone" /> {T('رخص الممارسين', 'Practitioner Licenses')}</CardTitle></CardHeader>
              <CardContent>
                {alerts.practitioner.length === 0 ? (
                  <p className="text-center text-[#2A8FA8]/60 py-6 text-sm">{T('لا توجد تنبيهات للممارسين', 'No practitioner alerts')}</p>
                ) : (
                  <div className="space-y-2">
                    {alerts.practitioner.map(d => (
                      <div key={d.id} className="flex items-center gap-3 p-3 bg-white border border-[#B0DDE8] rounded-lg" data-testid={`alert-pract-${d.id}`}>
                        <Warning size={20} weight="duotone" className={d.status === 'expired' ? 'text-red-600' : d.status === 'critical_7' ? 'text-red-500' : d.status === 'warning_30' ? 'text-orange-500' : 'text-yellow-500'} />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-[#2A8FA8] text-sm">{d.employee_name}</p>
                          <p className="text-xs text-[#2A8FA8]/60">{language === 'ar' ? d.doc_type_label_ar : d.doc_type_label_en} • <span dir="ltr">{d.expiry_date}</span></p>
                        </div>
                        <StatusBadge status={d.status} days={d.days_until_expiry} />
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* ============ Add/Edit Dialog ============ */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="sm:max-w-[560px] max-h-[90vh] flex flex-col" dir={direction} data-testid="doc-dialog">
            <div dir={direction} style={{ direction }} className="flex flex-col flex-1 min-h-0">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-[#2A8FA8]">
                  <FilePdf size={22} weight="duotone" />
                  {editing ? T('تعديل الوثيقة', 'Edit Document') :
                    dialogScope === 'establishment' ? T('وثيقة منشأة جديدة', 'New Establishment Document')
                                                    : T('رخصة ممارس جديدة', 'New Practitioner License')}
                </DialogTitle>
                <DialogDescription>
                  {dialogScope === 'practitioner' && selectedPract && !editing ?
                    T(`الممارس: ${selectedPract.name}`, `Practitioner: ${selectedPract.name}`) :
                    T('املأ بيانات الوثيقة وارفع نسخة (اختياري)', 'Fill in document details and upload a file (optional)')}
                </DialogDescription>
              </DialogHeader>

              <div className="flex-1 min-h-0 overflow-y-auto pe-1 mt-3 space-y-3">
                <div className="space-y-2">
                  <Label className="text-xs">{T('نوع الوثيقة *', 'Document Type *')}</Label>
                  <Select value={form.doc_type} onValueChange={v => setForm(p => ({ ...p, doc_type: v }))}>
                    <SelectTrigger data-testid="doc-type-select"><SelectValue placeholder={T('اختر النوع', 'Select type')} /></SelectTrigger>
                    <SelectContent>
                      {dialogScope === 'establishment'
                        ? Object.entries(docTypes.establishment || {}).map(([key, lbl]) => (
                            <SelectItem key={key} value={key}>{lbl[language] || key}</SelectItem>
                          ))
                        : (
                          // For practitioner scope we render the grouped
                          // catalogue with category headers. When the
                          // dialog was opened via an "Upload" shortcut
                          // we narrow the list to that single category.
                          Object.entries(docTypes.staff_categories || {})
                            .filter(([catId]) => !dialogPresetCategory || catId === dialogPresetCategory)
                            .map(([catId, cat]) => (
                              <div key={catId}>
                                <div className="px-2 py-1 text-[10px] font-semibold text-[#2A8FA8] bg-[#E8F7FB] uppercase tracking-wide">
                                  {cat[language]}
                                </div>
                                {cat.docs.map((d) => (
                                  <SelectItem key={d.id} value={d.id}>{d[language]}</SelectItem>
                                ))}
                              </div>
                            ))
                        )}
                    </SelectContent>
                  </Select>
                </div>

                {form.doc_type === 'other' && (
                  <div className="space-y-2">
                    <Label className="text-xs">{T('وصف مخصص', 'Custom Label')}</Label>
                    <Input value={form.custom_label} onChange={e => setForm(p => ({ ...p, custom_label: e.target.value }))} data-testid="custom-label-input" />
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-xs">{T('رقم الوثيقة', 'Document Number')}</Label>
                    <Input value={form.document_number} dir="ltr" onChange={e => setForm(p => ({ ...p, document_number: e.target.value }))} data-testid="doc-number-input" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">{T('جهة الإصدار', 'Issuer')}</Label>
                    <Input value={form.issuer} onChange={e => setForm(p => ({ ...p, issuer: e.target.value }))} data-testid="doc-issuer-input" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-xs">{T('تاريخ الإصدار', 'Issue Date')}</Label>
                    <Input type="date" value={form.issue_date} onChange={e => setForm(p => ({ ...p, issue_date: e.target.value }))} data-testid="issue-date-input" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">{T('تاريخ الانتهاء', 'Expiry Date')}</Label>
                    <Input type="date" value={form.expiry_date} onChange={e => setForm(p => ({ ...p, expiry_date: e.target.value }))} data-testid="expiry-date-input" />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs">{T('ملاحظات', 'Notes')}</Label>
                  <Input value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} data-testid="notes-input" />
                </div>

                <div className="space-y-2">
                  <Label className="text-xs">{T('رفع ملف (PDF/JPG/PNG، حد أقصى 10MB)', 'Upload File (PDF/JPG/PNG, max 10MB)')}</Label>
                  <Input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={e => setPickedFile(e.target.files?.[0] || null)} data-testid="file-input" />
                  {editing?.file && !pickedFile && (
                    <p className="text-[10px] text-[#2A8FA8]/60">{T('ملف موجود:', 'Existing file:')} {editing.file.original_name}</p>
                  )}
                </div>
              </div>

              <DialogFooter className="mt-3 pt-3 border-t border-[#B0DDE8]/30 shrink-0">
                <Button onClick={handleSave} disabled={saving} className="bg-[#2A8FA8] hover:bg-[#1F7A90] gap-2" data-testid="save-doc-btn">
                  <FloppyDisk size={16} weight="bold" /> {saving ? '...' : T('حفظ', 'Save')}
                </Button>
                <Button variant="outline" onClick={() => setShowDialog(false)}>{T('إلغاء', 'Cancel')}</Button>
              </DialogFooter>
            </div>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
